import React from 'react';
import { Box, Flex, Text, Icon, Badge, Progress, Tooltip } from '@chakra-ui/react';
import {
  Buildings,
  CurrencyDollarSimple,
  ChartLineUp,
  Warning,
  WarningCircle,
  XCircle,
  TrendDown,
} from 'phosphor-react';
import { ITaxRegime } from '../../../../../hooks/useCompanies';
import { formatCNPJ, toCurrency } from '../../../../../utils/FormatFunctions';
import { getProgressColor } from '../../../../../utils/HandleFunctions';

interface TaxRegimeCardProps {
  company: ITaxRegime;
  // eslint-disable-next-line no-unused-vars
  onClick: (company: ITaxRegime) => void;
}

// Função para detectar alertas da empresa
const getCompanyAlerts = (company: ITaxRegime) => {
  const alerts: Array<{
    type: 'error' | 'warning' | 'info';
    icon: typeof Warning;
    message: string;
    color: string;
  }> = [];

  // Limite excedido (100%+)
  if (company.limite_atingido >= 100) {
    alerts.push({
      type: 'error',
      icon: XCircle,
      message: 'Limite do regime excedido!',
      color: 'red',
    });
  }
  // Limite próximo (80%+)
  else if (company.limite_atingido >= 80) {
    alerts.push({
      type: 'warning',
      icon: WarningCircle,
      message: 'Próximo do limite do regime',
      color: 'orange',
    });
  }

  // Faturamento muito baixo (menos de 1000 no ano pode indicar problema)
  if (company.faturamento_acumulado > 0 && company.faturamento_acumulado < 1000) {
    alerts.push({
      type: 'info',
      icon: TrendDown,
      message: 'Faturamento muito baixo',
      color: 'yellow',
    });
  }

  // Dados inconsistentes
  if (!company.faixa_atual || company.faixa_atual === 'N/A') {
    alerts.push({
      type: 'warning',
      icon: Warning,
      message: 'Regime tributário não identificado',
      color: 'purple',
    });
  }

  return alerts;
};

export function TaxRegimeCard({ company, onClick }: TaxRegimeCardProps) {
  const alerts = getCompanyAlerts(company);
  const hasError = alerts.some(a => a.type === 'error');
  const hasWarning = alerts.some(a => a.type === 'warning');

  // Determina a cor da borda baseado nos alertas
  const getBorderColor = () => {
    if (hasError) return 'red.400';
    if (hasWarning) return 'orange.300';
    return 'gray.200';
  };

  return (
    <Box
      bg="white"
      borderRadius="xl"
      border="2px"
      borderColor={getBorderColor()}
      p={4}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        borderColor: (() => {
          if (hasError) return 'red.500';
          if (hasWarning) return 'orange.400';
          return 'blue.300';
        })(),
        boxShadow: 'md',
        transform: 'translateY(-2px)',
      }}
      onClick={() => onClick(company)}
    >
      {/* Header com ID e Alertas */}
      <Flex justify="space-between" align="flex-start" mb={3} gap={2}>
        <Text fontSize="sm" fontWeight="bold" color="gray.600">
          ID: {company.external_id}
        </Text>
        {/* Indicadores de Alerta */}
        {alerts.length > 0 && (
          <Flex gap={1} flexShrink={0}>
            {alerts.map(alert => (
              <Tooltip
                key={`${alert.type}-${alert.color}`}
                label={alert.message}
                hasArrow
                placement="top"
                bg={`${alert.color}.600`}
              >
                <Box
                  bg={`${alert.color}.100`}
                  p={1}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={alert.icon} boxSize={4} color={`${alert.color}.600`} weight="fill" />
                </Box>
              </Tooltip>
            ))}
          </Flex>
        )}
      </Flex>

      {/* Tags e Regime */}
      <Flex gap={2} flexWrap="wrap" mb={3}>
        {company.tags && (
          <Badge colorScheme="blue" fontSize="2xs" px={2} borderRadius="full" maxW="120px">
            <Text noOfLines={1} title={company.tags}>
              {company.tags}
            </Text>
          </Badge>
        )}
        {company.faixa_atual &&
          (() => {
            // Usa faixa_atual que já vem corretamente do backend ("Simples Nacional" ou "Lucro Presumido")
            const isSimplesNacional = company.faixa_atual === 'Simples Nacional';

            const isLucroPresumido = company.faixa_atual === 'Lucro Presumido';

            if (isSimplesNacional) {
              return (
                <Badge colorScheme="purple" fontSize="2xs" px={2} borderRadius="full" maxW="120px">
                  <Text noOfLines={1} title={company.faixa_atual}>
                    Simples Nacional
                  </Text>
                </Badge>
              );
            }
            if (isLucroPresumido) {
              return (
                <Badge colorScheme="orange" fontSize="2xs" px={2} borderRadius="full" maxW="120px">
                  <Text noOfLines={1} title={company.faixa_atual}>
                    Lucro Presumido
                  </Text>
                </Badge>
              );
            }
            return null;
          })()}
      </Flex>

      {/* Nome da Empresa */}
      <Flex align="center" gap={2} mb={3}>
        <Icon as={Buildings} color="blue.500" boxSize={5} />
        <Text fontSize="md" fontWeight="semibold" color="gray.800" noOfLines={2}>
          {company.razao_social}
        </Text>
      </Flex>

      {/* CNPJ */}
      <Text fontSize="xs" color="gray.500" mb={3} fontFamily="mono">
        {formatCNPJ(company.cnpj)}
      </Text>

      {/* Grid de informações */}
      <Box borderTop="1px" borderColor="gray.100" pt={3}>
        <Flex wrap="wrap" gap={3} direction="column">
          {/* Faturamento Acumulado */}
          <Flex align="center" gap={2} bg="blue.50" px={2} py={1} borderRadius="md">
            <Icon as={CurrencyDollarSimple} color="blue.500" boxSize={3} />
            <Text fontSize="xs" color="blue.700" fontWeight="medium">
              {toCurrency(company.faturamento_acumulado)}
            </Text>
          </Flex>

          {/* % do Limite */}
          <Box bg="gray.50" px={2} py={2} borderRadius="md">
            <Flex align="center" justify="space-between" mb={1}>
              <Flex align="center" gap={1}>
                <Icon as={ChartLineUp} color="gray.600" boxSize={3} />
                <Text fontSize="xs" color="gray.600" fontWeight="medium">
                  % do Limite
                </Text>
              </Flex>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color={(() => {
                  if (company.limite_atingido >= 80) return 'red.500';
                  if (company.limite_atingido >= 60) return 'orange.500';
                  return 'green.500';
                })()}
              >
                {company.limite_atingido}%
              </Text>
            </Flex>
            <Progress
              value={company.limite_atingido}
              size="sm"
              colorScheme={getProgressColor(company.limite_atingido)}
              borderRadius="full"
            />
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}
